import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import ReactTooltip from "react-tooltip";
import { Link, useHistory } from "react-router-dom";

// Custom Components
import * as baseAction from "store/base/base.action";
import * as customersAction from "store/customers/customers.action";
import * as subscriptionsAction from "store/subscriptions/subscriptions.action";
import AddContactPersonModal from "../modals/add-contact/add-contact-modal.component";
import DefaultLayout from "layouts/default/default.layout";
import CustomerTabs from "./add-customer-tabs/add-customer-tabs.container";
import CustomerInfo from "./customer-info/customer-info.container";
import SubscriptionAndBillingInfo from "./subscription-and-billing-info/subscription-and-billing-info.container";
import notification from "helpers/notification/notification.helper";
const AddCustomer = () => {
  const history = useHistory();
  const dispatch = useDispatch();

  const [awaitingSubscriptionAfterCustomer, setAwaitingSubscriptionAfterCustomer] =
    useState(false);
  const [provisioningInProgress, setProvisioningInProgress] = useState(false);
  const [subscriptionStepEnabled, setSubscriptionStepEnabled] = useState(false);
  const [showAddContactPersonModal, setShowAddContactPersonModal] =
    useState(false);
  const [errors, setErrors] = useState({ error: [] });
  const [contactPerson, setContactPerson] = useState({
    name: "",
    email: "",
    phone: "",
    primary: false
  });

  //Initial Customer Data
  const initCustomer = {
    id: "",
    company_full_legal_name: "",
    company_display_name: "",
    subdomain: "",
    domain: "",
    primary_admin_email: "",
    primary_admin_firstname: "",
    primary_admin_lastname: "",
    default_time_zone: "",
    default_language: "English",
    status: "Instance not created",
    contact_persons: [],
    tags: []
  };

  //Initial Subscription And Billing Data
  const initSubscriptionAndBillingInfo = {
    customer: "",
    plan: "",
    currency: "",
    subscription_status: "",
    start_date: "",
    end_date: "",
    increment_percentage: "",
    amount: "",
    payment_mode: "",
    payment_mode_details: "",
    coupon: ""
  };

  const [customer, setCustomer] = useState(initCustomer);
  const [subscriptionAndBilling, setSubscriptionAndBilling] = useState(
    initSubscriptionAndBillingInfo
  );
  const subscriptionPayloadRef = useRef(null);
  const submitFlowActiveRef = useRef(false);
  const isValidEmail = value => {
    const emailRegex =
      /^(?:[a-zA-Z0-9_'^&\/+\-])+(?:\.(?:[a-zA-Z0-9_'^&\/+\-])+)*@(?:[a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}$/;
    return emailRegex.test(String(value || "").trim());
  };

  const addCustomerResponse = useSelector(
    state => state.customers[customersAction.ADD_CUSTOMER]
  );
  const isCustomerLoading = useSelector(
    state => state.customers[customersAction.CUSTOMER_ADD_LOADING]
  );
  const customerAddErrors = useSelector(
    state => state.customers[customersAction.CUSTOMER_ADD_ERROR]
  );
  const addSubscriptionResponse = useSelector(
    state => state.subscriptions[subscriptionsAction.ADD_SUBSCRIPTION]
  );
  const isSubscriptionLoading = useSelector(
    state => state.subscriptions[subscriptionsAction.SUBSCRIPTION_ADD_LOADING]
  );
  const subscriptionErrors = useSelector(
    state => state.subscriptions[subscriptionsAction.SUBSCRIPTION_ADD_ERROR]
  );
  useEffect(() => {
    dispatch(baseAction.reset(customersAction.ADD_CUSTOMER));
    dispatch(baseAction.reset(customersAction.CUSTOMER_ADD_ERROR));
    dispatch(baseAction.reset(subscriptionsAction.ADD_SUBSCRIPTION));
    dispatch(baseAction.reset(subscriptionsAction.SUBSCRIPTION_ADD_ERROR));
  }, [dispatch]);

  useEffect(() => {
    if (
      history.location.pathname.search("subscription-and-billing-info") !== -1 &&
      !subscriptionStepEnabled
    ) {
      history.replace("/manage-customers/add-customer-info");
    }
  }, [history, subscriptionStepEnabled]);

  // Final submit: customer created, then subscription (use ref — stale closure if reading state here)
  useEffect(() => {
    const customerId =
      addCustomerResponse?.data?.id || addCustomerResponse?.data?._id;
    if (!awaitingSubscriptionAfterCustomer || !customerId) {
      return;
    }
    const subscriptionPayload = subscriptionPayloadRef.current;
    if (!subscriptionPayload?.plan) {
      submitFlowActiveRef.current = false;
      setAwaitingSubscriptionAfterCustomer(false);
      setProvisioningInProgress(false);
      notification.error(
        [{ message: "Subscription details were lost. Please fill the form and submit again." }],
        { position: "top-left" }
      );
      return;
    }
    setCustomer(prevState => ({
      ...prevState,
      id: customerId
    }));
    setSubscriptionAndBilling(prevState => ({
      ...prevState,
      customer: customerId
    }));
    setAwaitingSubscriptionAfterCustomer(false);
    dispatch(baseAction.reset(customersAction.ADD_CUSTOMER));
    dispatch(
      subscriptionsAction.addSubscription({
        ...subscriptionPayload,
        customer: customerId,
        skip_audit_log: true
      })
    );
  }, [addCustomerResponse, awaitingSubscriptionAfterCustomer, dispatch]);

  useEffect(() => {
    if (customerAddErrors && awaitingSubscriptionAfterCustomer) {
      submitFlowActiveRef.current = false;
      setAwaitingSubscriptionAfterCustomer(false);
      setProvisioningInProgress(false);
      notification.error(customerAddErrors, {
        position: "top-left"
      });
      dispatch(baseAction.reset(customersAction.CUSTOMER_ADD_ERROR));
    }
  }, [customerAddErrors, awaitingSubscriptionAfterCustomer, dispatch]);

  // When the component is unloaded, reset the error messages
  useEffect(() => {
    return () => {
      dispatch(baseAction.reset(customersAction.ADD_CUSTOMER));
      dispatch(baseAction.reset(customersAction.UPDATE_CUSTOMER));
      dispatch(baseAction.reset(customersAction.CUSTOMER_ADD_ERROR));
      dispatch(baseAction.reset(customersAction.UPDATE_CUSTOMER_ERROR));
      dispatch(baseAction.reset(subscriptionsAction.ADD_SUBSCRIPTION));
      dispatch(baseAction.reset(subscriptionsAction.SUBSCRIPTION_ADD_ERROR));
    };
  }, [dispatch]);

  // When the subscription creation is successful (final step of add-customer flow)
  useEffect(() => {
    if (!addSubscriptionResponse?.data) {
      return;
    }

    const apiNotifications = addSubscriptionResponse?.notifications;
    const successMessages =
      (Array.isArray(apiNotifications) && apiNotifications.length > 0) ||
      (typeof apiNotifications === "string" && apiNotifications.trim())
        ? apiNotifications
        : ["Customer created successfully"];

    notification.success(successMessages, {
      position: "top-left"
    });

    setCustomer(initCustomer);
    setSubscriptionAndBilling(initSubscriptionAndBillingInfo);
    setSubscriptionStepEnabled(false);
    setAwaitingSubscriptionAfterCustomer(false);
    setProvisioningInProgress(false);
    submitFlowActiveRef.current = false;

    history.push("/manage-customers");

    dispatch(baseAction.reset(subscriptionsAction.ADD_SUBSCRIPTION));
    dispatch(baseAction.reset(subscriptionsAction.SUBSCRIPTION_ADD_ERROR));
  }, [addSubscriptionResponse, dispatch, history]);

  // When there is an error adding the subscription
  useEffect(() => {
    if (!submitFlowActiveRef.current || !subscriptionErrors) {
      return;
    }
    notification.error(subscriptionErrors, {
      position: "top-left"
    });
    submitFlowActiveRef.current = false;
    setAwaitingSubscriptionAfterCustomer(false);
    setProvisioningInProgress(false);
    dispatch(baseAction.reset(subscriptionsAction.SUBSCRIPTION_ADD_ERROR));
  }, [subscriptionErrors, dispatch]);

  const handleAddContactPerson = contactPerson => {
    if (contactPerson?.name?.length === 0) {
      setErrors({
        ...errors,
        error: [{ message: "Full Name is required", field: "name" }]
      });
      return;
    }
    if (contactPerson?.email?.length === 0) {
      setErrors({
        ...errors,
        error: [{ message: "Email is required", field: "email" }]
      });
      return;
    }
    if (!isValidEmail(contactPerson?.email)) {
      setErrors({
        ...errors,
        error: [{ message: "Email address is invalid format", field: "email" }]
      });
      return;
    }
    if (contactPerson?.phone?.length === 0) {
      setErrors({
        ...errors,
        error: [{ message: "Phone is required", field: "number" }]
      });
      return;
    }
    // Check if the name already exists
    const nameExists = customer?.contact_persons.some(
      p => p.name === contactPerson.name
    );
    if (nameExists) return;
    // Check if the email already exists
    const emailExists = customer?.contact_persons.some(
      p => p.email === contactPerson.email
    );
    if (emailExists) return;
    // Check if the phoneNumber already exists
    const numberExists = customer?.contact_persons.some(
      p => p.phone === contactPerson.phone
    );
    if (numberExists) return;
    setCustomer({
      ...customer,
      contact_persons: [...customer?.contact_persons, contactPerson]
    });
    setContactPerson({
      name: "",
      email: "",
      phone: "",
      primary: false
    });
    setShowAddContactPersonModal(false);
  };

  const handleRemoveContactPerson = contactPerson => {
    if (!contactPerson) return;
    const newContacts = customer?.contact_persons.filter(
      c => c.name !== contactPerson.name
    );
    setCustomer({
      ...customer,
      contact_persons: newContacts
    });
  };

  const handleContinueToSubscription = () => {
    setSubscriptionStepEnabled(true);
    history.push("/manage-customers/subscription-and-billing-info");
  };

  const handleFinalSubmit = () => {
    subscriptionPayloadRef.current = { ...subscriptionAndBilling };
    submitFlowActiveRef.current = true;
    dispatch(baseAction.reset(subscriptionsAction.ADD_SUBSCRIPTION));
    dispatch(baseAction.reset(subscriptionsAction.SUBSCRIPTION_ADD_ERROR));
    dispatch(baseAction.reset(customersAction.CUSTOMER_ADD_ERROR));
    setProvisioningInProgress(true);
    setAwaitingSubscriptionAfterCustomer(true);
    dispatch(customersAction.addCustomer(customer));
  };

  const loadComponent = () => {
    const { pathname } = history.location;
    if (pathname.search("add-customer-info") !== -1) {
      return (
        <CustomerInfo
          customer={customer}
          setCustomer={setCustomer}
          setShowAddContactPersonModal={setShowAddContactPersonModal}
          handleRemoveContactPerson={handleRemoveContactPerson}
          contactPerson={contactPerson}
          onContinueToSubscription={handleContinueToSubscription}
        />
      );
    } else if (pathname.search(`subscription-and-billing-info`) !== -1) {
      return (
        <SubscriptionAndBillingInfo
          subscriptionAndBilling={subscriptionAndBilling}
          setSubscriptionAndBilling={setSubscriptionAndBilling}
          onFinalSubmit={handleFinalSubmit}
          isSubmitting={isCustomerLoading || isSubscriptionLoading}
        />
      );
    }
  };

  return (
    <DefaultLayout>
      {
        <AddContactPersonModal
          showAddContactPersonModal={showAddContactPersonModal}
          setShowAddContactPersonModal={setShowAddContactPersonModal}
          handleAddContactPerson={handleAddContactPerson}
          contactPerson={contactPerson}
          setContactPerson={setContactPerson}
          errors={errors?.error}
        />
      }
      <ReactTooltip />
      <div className="container">
        {/* Content Header */}
        <div className="app__data--listing-header">
          <div className="app__data--listing-header-top mb-20">
            <div className="app__data--listing-header-left-col">
              <div className="app__data--listing-title">
                <Link to="/manage-customers" className="mr-10">
                  <i
                    className="fas fa-long-arrow-alt-left"
                    data-tip="Back to Manage Customers"
                  ></i>
                </Link>
                Add Customer
              </div>
            </div>
          </div>
        </div>

        <div className="customer-info card">
          {/* Tabs */}
          <CustomerTabs subscriptionStepEnabled={subscriptionStepEnabled} />
          {loadComponent()}
        </div>
      </div>
    </DefaultLayout>
  );
};

export default AddCustomer;
